import {
	AddressItemType,
	ipmQueryClient,
	PostageUsageType,
	sendMail,
	uploadFile,
	useInfiniteAddresses,
	UserFileCategory,
} from '@ukpostbox/client-data';
import {
	ButtonSubmit,
	Card,
	CardLayoutType,
	ColourStateSolid,
	ContentSidebarLayout,
	SelectItemType,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import { FieldValues, FormProvider, useForm } from '@ukpostbox/frontend-framework/lib/ReactHookForm';
import React, { useEffect, useRef, useState } from 'react';
import { AddAddressModal } from '../../_Global/Components/Address/AddAddress/AddAddressModal';
import { AddressControl } from '../../_Global/Components/Address/AddressControl';
import { PostageControl } from '../../_Global/Components/PostageControl';
import { SendsFileControl } from '../Components/Controls/SendsFileControl';
import SendsNavigation from '../Components/Shared/Controls/SendsNavigation';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import StandaloneSendTemplateNavigation from '../../SendsProfiles/ProfilesAdmin/Components/StandaloneSendTemplateNavigation';
import SendProcessingWizardModal from '../../SendsProfiles/ProfilesAdmin/Components/Modals/SendProcessingWizardModal';
import { SendDataProcessType } from '../../SendsProfiles/ProfilesAdmin/Types/SendsFormTypes';

function SendMailPage() {
	const { t } = useTranslation();
	const { currentLocale, updatePageMeta } = useSite();
	const sendMailRef = useRef<any>(null);
	const sendMailNavigationRef = useRef<any>(null);

	const [addAddressActive, setAddAddressActive] = useState<boolean>(false);
	const [activeAddress, setActiveAddress] = useState<AddressItemType | null>(null);
	const [activePostage, setActivePostage] = useState<SelectItemType | null>(null);

	const { isLoading, data, error } = useInfiniteAddresses();
	const [addressOptions, setAddressOptions] = useState<Array<any>>([]);
	const [defaultAddress, setDefaultAddress] = useState<Array<number> | undefined>(undefined);

	const [sendError, setSendError] = useState<string | null>(null);
	const [reset, setReset] = useState<boolean>(false);

	const [sendProcessData, setSendProcessData] = useState<SendDataProcessType>({
		form: null,
		address: '',
		postage: '',

		sendProcessOpen: false,

		uploadFileActive: false,
		sendDataActive: false,

		postingData: false,

		sendComplete: false,
	});

	const formMethods = useForm({
		defaultValues: {
			address: -1,
			postage: -1,
			file: null,
		},
		mode: 'onChange',
	});

	const {
		handleSubmit,
		setValue,
		reset: formReset,
		formState: { isValid },
	} = formMethods;

	const FilterSelectedAddress = () => {
		const filteredAddress: Array<any> = [];

		data?.pages.forEach(
			(page) =>
				page?.data &&
				page.data.forEach((address) => {
					filteredAddress.push({ label: address.label, value: address.value });
				}),
		);

		setAddressOptions(filteredAddress);
	};

	const HandleProcessStart = (formData: FieldValues) => {
		setSendError(null);
		setSendProcessData({
			...sendProcessData,
			form: formData,
			sendProcessOpen: true,
			address: activeAddress?.label,
			postage: activePostage?.label,
		});
	};

	const HandleSend = (fileUuid: string) => {
		sendMail({
			fileUuid,
			notes: sendProcessData.form.notes,
			sendsPageId: sendProcessData.form.sendsPageId,
			addressId: sendProcessData.form.address,
			postageOptionId: sendProcessData.form.postage,
		})
			.then((s) => {
				setSendProcessData({
					...sendProcessData,
					sendDataActive: false,
					sendComplete: true,
					postingData: true,
					mailUuid: s.mailUuid,
				});
				ipmQueryClient.invalidateQueries({ queryKey: ['allSendsHistory'] }).then(() => null);
			})
			.catch((e) => {
				setSendError(e.toString());
				setSendProcessData({
					...sendProcessData,
					sendProcessOpen: false,
					uploadFileActive: false,
					sendDataActive: false,
					postingData: false,
				});
			});
	};

	const HandleUpload = () => {
		setSendProcessData({ ...sendProcessData, uploadFileActive: true, postingData: true });

		uploadFile({
			file: sendProcessData.form.file[0],
			fileCategory: UserFileCategory.MailSends,
		})
			.then((r) => {
				setSendProcessData({
					...sendProcessData,
					uploadFileActive: false,
					sendDataActive: true,
					postingData: true,
				});
				HandleSend(r.fileUuid);
			})
			.catch((e) => {
				setSendError(e.toString());
				setSendProcessData({
					...sendProcessData,
					sendProcessOpen: false,
					uploadFileActive: false,
					sendDataActive: false,
					postingData: false,
				});
			});
	};

	const ResetSend = () => {
		formReset();
		setReset(!reset);
		setSendProcessData({
			address: '',
			postage: '',
			form: null,

			sendProcessOpen: false,

			uploadFileActive: false,
			sendDataActive: false,

			postingData: false,
			sendComplete: false,
		});
	};

	useEffect(() => {
		if (data) {
			FilterSelectedAddress();
		}
	}, [data]);

	useEffect(() => {
		updatePageMeta({
			title: t('sends.title'),
		});
	}, [currentLocale]);

	return (
		<ContentSidebarLayout
			ref={sendMailRef}
			navigationComponent={<SendsNavigation ref={sendMailNavigationRef} title={t('sends.title')} />}
			extraContentClass='flex-column send-mail-container'
		>
			<div className='column-adjuster pl-md pr-md'>
				<div className='container-narrow'>
					<div className='row no-gap'>
						<div className='col-xs-12 col-xl-3'>
							<StandaloneSendTemplateNavigation />
						</div>
						<div className='col-xs-12 col-xl-6'>
							<Card header={{ title: '' }} type={CardLayoutType.SlimHeader}>
								<FormProvider {...formMethods}>
									<form onSubmit={handleSubmit((thisData) => HandleProcessStart(thisData))}>
										<fieldset>
											<h4 className='mb-0'>{t('sends.title')}</h4>
											<p>{t('sends.description')}</p>

											<hr />

											<div className='flex-column flex-gap-md'>
												<AddressControl
													name='address'
													singleSelection
													forwardItemCallback={(e) => {
														setValue('address', e.value);
														setActiveAddress(e);
													}}
													defaultValues={defaultAddress || undefined}
													showCreateAddressOptions
													hideImportOptions
													newAddressCallback={(e) => {
														setValue('address', e.addressId);
														setDefaultAddress([e.addressId]);
														setActiveAddress({
															value: e.addressId,
															label: e.addressString as string,
															object: e,
														});
													}}
													loading={isLoading}
													reset={reset}
												/>

												<PostageControl
													name='postage'
													postageType={PostageUsageType.Sends}
													activeAddress={activeAddress}
													singleSelection
													forwardValueCallback={(e) => {
														setValue('postage', e);
													}}
													forwardItemCallback={(e) => {
														setValue('postage', e.value);
														setActivePostage(e);
													}}
													reset={reset}
												/>

												<SendsFileControl name='file' reset={reset} />

												<ButtonSubmit
													title={t('sends.actions.send')}
													colour={ColourStateSolid.Primary}
													fullWidth
													disabled={!isValid}
												/>
											</div>
										</fieldset>
									</form>
								</FormProvider>
							</Card>
						</div>
					</div>
				</div>
			</div>

			<SendProcessingWizardModal
				isOpen={sendProcessData.sendProcessOpen as boolean}
				sendData={sendProcessData}
				actionedCallback={() => HandleUpload()}
				closeCallback={() =>
					sendProcessData.postingData
						? ResetSend()
						: setSendProcessData({ ...sendProcessData, sendProcessOpen: false })
				}
				lockOpen={sendProcessData.postingData}
			/>

			<AddAddressModal
				isOpen={addAddressActive}
				closeCallback={() => setAddAddressActive(false)}
				actionedCallback={(id: number) => null}
			/>
		</ContentSidebarLayout>
	);
}

export default SendMailPage;
